// Enhanced audio player utility with better error handling

export const playAudioFile = (audioUrl, options = {}) => {
  return new Promise((resolve, reject) => {
    console.log('Loading audio from:', audioUrl);
    
    const audio = new Audio();
    let hasStarted = false;
    let timeoutId = null;
    
    // Clean up function
    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
    
    // Set up event listeners before setting src
    audio.addEventListener('loadstart', () => {
      console.log('Audio load started');
    });

    audio.addEventListener('canplaythrough', () => {
      console.log('Audio can play through');
    });

    audio.addEventListener('loadeddata', () => {
      console.log('Audio data loaded');
    });

    audio.addEventListener('error', (e) => {
      cleanup();
      console.error('Audio error event:', e);
      console.error('Audio error details:', {
        code: audio.error?.code,
        message: audio.error?.message,
        networkState: audio.networkState,
        readyState: audio.readyState,
        src: audio.src
      });
      
      let errorMessage = 'Unknown audio error';
      if (audio.error) {
        switch (audio.error.code) {
          case 1: errorMessage = 'Audio loading was aborted'; break;
          case 2: errorMessage = 'Network error while loading audio'; break;
          case 3: errorMessage = 'Audio decoding error - format may not be supported by this browser'; break;
          case 4: errorMessage = 'Audio format not supported by this browser'; break;
          default: errorMessage = `Audio error (code: ${audio.error.code})`;
        }
      }
      reject(new Error(errorMessage));
    });

    audio.addEventListener('play', () => {
      console.log('Audio started playing');
      hasStarted = true;
    });

    audio.addEventListener('ended', () => {
      cleanup();
      console.log('Audio playback ended');
      resolve();
    });

    audio.addEventListener('pause', () => {
      if (hasStarted) {
        cleanup();
        console.log('Audio playback completed or paused');
        resolve();
      }
    });

    // Set audio properties with better browser compatibility
    audio.crossOrigin = 'anonymous'; // Handle CORS
    audio.preload = 'metadata'; // Changed from 'auto' to reduce loading issues
    
    // Add timeout for loading
    timeoutId = setTimeout(() => {
      cleanup();
      if (audio.readyState === 0) {
        reject(new Error('Audio loading timeout - file may be corrupted or server unavailable'));
      }
    }, 15000); // Increased to 15 second timeout

    // Set source and load
    audio.src = audioUrl;
    
    // Try to load the audio
    try {
      audio.load();
    } catch (loadError) {
      cleanup();
      console.error('Audio load failed:', loadError);
      reject(new Error(`Failed to load audio: ${loadError.message}`));
      return;
    }

    // Attempt to play with better error handling
    const attemptPlay = async () => {
      try {
        const playPromise = audio.play();
        
        if (playPromise !== undefined) {
          await playPromise;
          console.log('Audio play started successfully');
        }
      } catch (playError) {
        cleanup();
        console.error('Audio play failed:', playError);
        
        // Handle specific "operation not supported" errors
        if (playError.name === 'NotSupportedError' || 
            playError.message.includes('operation is not supported') ||
            playError.message.includes('not supported')) {
          reject(new Error('Audio format not supported by this browser. Try using a different browser or audio format.'));
        } else if (playError.name === 'NotAllowedError') {
          reject(new Error('Audio playback blocked by browser. Please interact with the page first.'));
        } else {
          reject(new Error(`Playback failed: ${playError.message}`));
        }
      }
    };

    // Wait a bit for the audio to load before attempting to play
    setTimeout(attemptPlay, 100);
  });
};

export const testAudioUrl = async (audioUrl) => {
  try {
    // Use Range request to get just the first few bytes (more reliable than HEAD)
    const response = await fetch(audioUrl, { 
      method: 'GET',
      headers: {
        'Range': 'bytes=0-1023' // Get first 1KB
      }
    });
    
    const contentType = response.headers.get('content-type');
    const isAudioFile = contentType && contentType.startsWith('audio/');
    
    return {
      accessible: response.ok,
      status: response.status,
      contentType,
      contentLength: response.headers.get('content-length'),
      isAudioFile,
      partialContent: response.status === 206 // Range request supported
    };
  } catch (error) {
    return {
      accessible: false,
      error: error.message,
      isAudioFile: false
    };
  }
};

// Test browser audio format support
export const testBrowserAudioSupport = () => {
  const audio = new Audio();
  
  return {
    mp3: audio.canPlayType('audio/mpeg') !== '',
    wav: audio.canPlayType('audio/wav') !== '',
    m4a: audio.canPlayType('audio/mp4') !== '',
    aac: audio.canPlayType('audio/aac') !== '',
    ogg: audio.canPlayType('audio/ogg') !== '',
    webm: audio.canPlayType('audio/webm') !== '',
    flac: audio.canPlayType('audio/flac') !== ''
  };
};

// Get recommended audio format for current browser
export const getRecommendedAudioFormat = () => {
  const support = testBrowserAudioSupport();
  
  // Prefer MP3 for widest compatibility
  if (support.mp3) return 'mp3';
  if (support.wav) return 'wav';
  if (support.m4a) return 'm4a';
  if (support.ogg) return 'ogg';
  if (support.webm) return 'webm';
  
  return 'mp3'; // Default fallback
};